import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './Select2Dropdown.css';

const Select2Dropdown = ({ 
    options = [], 
    value = '', 
    onChange, 
    placeholder = 'Select an option...', 
    disabled = false, 
    required = false,
    displayKey = null, // For objects: key to display
    valueKey = null,   // For objects: key to use as value
    className = '',
    searchable = true,
    clearable = false,
    maxHeight = '200px',
    noOptionsMessage = 'No results found',
    onDropdownOpen,
    allowCreate = false,
    menuPlacement = 'bottom', // 'auto', 'bottom', 'top'
    onCreateTag,
    popoutMode = false, // Higher z-index for menus rendered inside PopoutWindow windows
    hidden = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredOptions, setFilteredOptions] = useState(options);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const searchInputRef = useRef(null);
    const dropdownRef = useRef(null);
    const selectionRef = useRef(null); // the visible control
    const menuRef = useRef(null);      // the floating menu
    const [menuStyle, setMenuStyle] = useState(null);

    const getMenuStyle = () => {
        if (!selectionRef.current) {
            return null;
        }

        // The component may render inside a window.open popout via a portal —
        // measure against the document/window that actually owns the element.
        const view = (selectionRef.current.ownerDocument && selectionRef.current.ownerDocument.defaultView) || window;
        const viewHeight = view.innerHeight;

        const rect = selectionRef.current.getBoundingClientRect();
        // The menu is portaled to the owning document's body, so it no longer
        // inherits fonts from .select2-dropdown — copy the control's computed font.
        const computed = view.getComputedStyle(selectionRef.current);
        const fontStyle = { fontFamily: computed.fontFamily, fontSize: computed.fontSize };
        const maxHeightPx = parseInt(maxHeight, 10) || 250;
        // Use maxHeightPx as the required space threshold (worst-case full menu height).
        // For 'auto': open upward whenever there isn't enough room below, regardless of
        // whether spaceAbove is larger — prevents the menu from being clipped by the
        // bottom of a modal or the viewport edge.
        const showTop = menuPlacement === 'top' || (menuPlacement === 'auto' && (viewHeight - rect.bottom) < maxHeightPx);
        const zIndex = popoutMode ? 999999 : 99999;

        // Fixed to the element's own viewport (popup or main window) so the menu
        // escapes overflow:auto/hidden ancestors and stays in front of page content.
        if (showTop) {
            return {
                position: 'fixed',
                top: 'auto',
                bottom: `${viewHeight - rect.top + 2}px`,
                left: `${rect.left}px`,
                right: 'auto',
                width: `${rect.width}px`,
                zIndex,
                ...fontStyle,
            };
        }

        return {
            position: 'fixed',
            top: `${rect.bottom + 2}px`,
            bottom: 'auto',
            left: `${rect.left}px`,
            right: 'auto',
            width: `${rect.width}px`,
            zIndex,
            ...fontStyle,
        };
    };

    useEffect(() => {
        if (!searchTerm) {
            setFilteredOptions(options);
            return;
        }

        const filtered = options.filter(option => {
            const displayText = getDisplayText(option);
            return displayText.toLowerCase().includes(searchTerm.toLowerCase());
        });
        setFilteredOptions(filtered);
    }, [options, searchTerm]);

    useEffect(() => {
        if (isOpen && searchInputRef.current && searchable) {
            searchInputRef.current.focus();
        }
    }, [isOpen, searchable]);

    // Close when clicking outside selection or the floating menu (so menu clicks don't close it)
    useEffect(() => {
        const handleClickOutside = (event) => {
            const t = event.target;
            const insideSelection = selectionRef.current && selectionRef.current.contains(t);
            const insideMenu = menuRef.current && menuRef.current.contains(t);
            if (!insideSelection && !insideMenu) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };
        // Attach to the document that owns the element — the opener's document
        // never sees mousedown events fired inside a window.open popout.
        const doc = (dropdownRef.current && dropdownRef.current.ownerDocument) || document;
        doc.addEventListener('mousedown', handleClickOutside);
        return () => doc.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Reposition floating menu when open / on scroll / resize
    useEffect(() => {
        const reposition = () => {
            if (!isOpen) {
                setMenuStyle(null);
                return;
            }
            setMenuStyle(getMenuStyle());
        };
        if (isOpen) {
            reposition();
            // Popup-window scrolls don't reach the opener's window — listen on the
            // window that owns the element, capturing inner scroll containers too.
            const view = (dropdownRef.current && dropdownRef.current.ownerDocument && dropdownRef.current.ownerDocument.defaultView) || window;
            view.addEventListener('scroll', reposition, true);
            view.addEventListener('resize', reposition);
            return () => {
                view.removeEventListener('scroll', reposition, true);
                view.removeEventListener('resize', reposition);
            };
        }
        return undefined;
    }, [isOpen, menuPlacement, maxHeight]);

    const handleSearch = (term) => {
        setSearchTerm(term);
        if (!term) {
            setFilteredOptions(options);
            return;
        }

        const filtered = options.filter(option => {
            const displayText = getDisplayText(option);
            return displayText.toLowerCase().includes(term.toLowerCase());
        });
        setFilteredOptions(filtered);
        setFocusedIndex(-1);
    };

    const getDisplayText = (option) => {
        if (typeof option === 'string') return option;
        if (typeof option === 'object' && displayKey) {
            // Handle nested keys like "name.common"
            const keys = displayKey.split('.');
            let value = option;
            for (const key of keys) {
                value = value?.[key];
                if (value === undefined) return option.toString();
            }
            return value;
        }
        return option?.toString() || '';
    };

    const getValue = (option) => {
        if (typeof option === 'string') return option;
        if (typeof option === 'object' && valueKey) {
            // Handle nested keys like "id"
            const keys = valueKey.split('.');
            let value = option;
            for (const key of keys) {
                value = value?.[key];
                if (value === undefined) return option;
            }
            return value;
        }
        return option;
    };

    const getSelectedDisplayText = () => {
        if (!value) return placeholder;
        const selectedOption = options.find(option => getValue(option) === value);
        return selectedOption ? getDisplayText(selectedOption) : placeholder;
    };

    const handleOptionClick = (option) => {
        const optionValue = getValue(option);
        onChange(optionValue);
        setIsOpen(false);
        setSearchTerm('');
        setFocusedIndex(-1);
    };

    const handleToggle = () => {
        if (!disabled) {
            const willOpen = !isOpen;
            setIsOpen(willOpen);
            setSearchTerm('');
            if (willOpen && typeof onDropdownOpen === 'function') {
                onDropdownOpen();
            }
            // Compute initial menu style immediately; resize/scroll effect will keep it synced.
            if (willOpen) {
                setMenuStyle(getMenuStyle());
            } else {
                setMenuStyle(null);
            }
        }
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange('');
        setIsOpen(false);
        setSearchTerm('');
        setFocusedIndex(-1);
    };

    const handleCreateAndSelect = async (term) => {
        if (typeof onCreateTag === 'function') {
            const newOption = await onCreateTag(term);
            if (newOption) {
                // If the create function returns the new object, select it
                handleOptionClick(newOption);
            } else {
                // Fallback if nothing is returned
                setIsOpen(false);
                setSearchTerm('');
            }
        } else {
            setIsOpen(false);
            setSearchTerm('');
        }
    };

    const handleKeyDown = async (e) => {
        if (e.key === 'Escape') {
            setIsOpen(false);
            setSearchTerm('');
            setFocusedIndex(-1);
        } else if (e.key === 'Enter' && !isOpen) {
            e.preventDefault();
            setIsOpen(true);
        } else if (e.key === 'ArrowDown' && !isOpen) {
            e.preventDefault();
            setIsOpen(true);
        } else if (e.key === 'Enter' && isOpen) {
            e.preventDefault();
            if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
                handleOptionClick(filteredOptions[focusedIndex]);
            } else if (searchTerm && allowCreate && filteredOptions.length === 0) {
                await handleCreateAndSelect(searchTerm);
            }
        } else if (e.key === 'ArrowDown' && isOpen) {
            e.preventDefault();
            setFocusedIndex(prev => Math.min(prev + 1, filteredOptions.length - 1));
        } else if (e.key === 'ArrowUp' && isOpen) {
            e.preventDefault();
            setFocusedIndex(prev => Math.max(prev - 1, 0));
        }
    };

    // Scroll focused option into view
    useEffect(() => {
        if (isOpen && focusedIndex >= 0 && menuRef.current) {
            const optionElements = menuRef.current.querySelectorAll('.select2-results__option:not(.select2-results__option--no-results):not(.select2-results__option--create)');
            if (optionElements[focusedIndex]) {
                optionElements[focusedIndex].scrollIntoView({
                    block: 'nearest',
                    behavior: 'smooth'
                });
            }
        }
    }, [focusedIndex, isOpen]);


    return (
            
            <div className={`select2-dropdown ${className}`} ref={dropdownRef} style={{ position: 'relative', overflow: 'visible' }}>
                {!hidden && (
                    <div 
                        ref={selectionRef}
                        className={`select2-selection ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''} ${required && !value ? 'required' : ''}`}
                        onClick={handleToggle}
                        onKeyDown={handleKeyDown}
                        tabIndex={disabled ? -1 : 0}
                        style={{ position: 'relative' }}
                    >
                        {required && (
                            <input
                                type="text"
                                required={!disabled}
                                disabled={disabled}
                                value={value === undefined || value === null || value === '' ? '' : value}
                                onChange={() => {}}
                                tabIndex={-1}
                                aria-hidden="true"
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    opacity: 0,
                                    pointerEvents: 'none',
                                    border: 'none',
                                    padding: 0,
                                    margin: 0
                                }}
                            />
                        )}
                        <span className="select2-selection__rendered">
                            {getSelectedDisplayText()}
                        </span>
                        <span className="select2-selection__actions">
                            {clearable && value && !disabled && (
                                <span className="select2-selection__clear" onClick={handleClear}>
                                    ×
                                </span>
                            )}
                            <span className="select2-selection__arrow">
                                <span className={`select2-arrow ${isOpen ? 'open' : ''}`}>▼</span>
                            </span>
                        </span>
                    </div>
                )}

                {isOpen && !disabled && !hidden && createPortal(
                    <div className="select2-dropdown-menu" ref={menuRef} style={menuStyle || getMenuStyle()}>
                        {searchable && (
                            <div className="select2-search">
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    className="select2-search__field"
                                    placeholder="Search..."
                                    value={searchTerm}
                                    onChange={(e) => handleSearch(e.target.value)}
                                    onKeyDown={async (e) => {
                                        if (e.key === 'Escape') {
                                            setIsOpen(false);
                                            setSearchTerm('');
                                            setFocusedIndex(-1);
                                        } else if (e.key === 'Enter') {
                                            e.preventDefault();
                                            if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
                                                handleOptionClick(filteredOptions[focusedIndex]);
                                            } else if (searchTerm && allowCreate && filteredOptions.length === 0) {
                                                await handleCreateAndSelect(searchTerm);
                                            }
                                        } else if (e.key === 'ArrowDown') {
                                            e.preventDefault();
                                            setFocusedIndex(prev => Math.min(prev + 1, filteredOptions.length - 1));
                                        } else if (e.key === 'ArrowUp') {
                                            e.preventDefault();
                                            setFocusedIndex(prev => Math.max(prev - 1, 0));
                                        }
                                    }}
                                />
                            </div>
                        )}
                        <div className="select2-results" style={{ maxHeight }}>
                            {filteredOptions.length > 0 ? (
                                filteredOptions.map((option, index) => (
                                    <div
                                        key={index}
                                        className={`select2-results__option ${getValue(option) === value ? 'selected' : ''} ${focusedIndex === index ? 'focused' : ''}`}
                                        onClick={() => handleOptionClick(option)}
                                        title={getDisplayText(option)}
                                    >
                                        {getDisplayText(option)}
                                    </div>
                                ))
                            ) : (
                                allowCreate && searchTerm ? (
                                    <div
                                        className="select2-results__option select2-results__option--create"
                                        onClick={async () => {
                                            await handleCreateAndSelect(searchTerm);
                                        }}
                                    >
                                        Create "<strong>{searchTerm}</strong>"
                                    </div>
                                ) : (
                                    <div className="select2-results__option select2-results__option--no-results">
                                        {noOptionsMessage}
                                    </div>
                                )
                            )}
                        </div>
                    </div>,
                    // Portal into the document that owns this element so the menu
                    // escapes ancestor stacking contexts (z-index tables, modals)
                    // and clips — works for both the main window and PopoutWindow.
                    (dropdownRef.current && dropdownRef.current.ownerDocument && dropdownRef.current.ownerDocument.body) || document.body
                )}
            </div>
    );
};

export default Select2Dropdown;